// Alias the statement's unaliased relations in the same edit as a JOIN that
// introduces an alias.
//
// `SELECT id, name FROM customers` + `JOIN orders o …` reads two ways: a bare
// base table next to an aliased join. The VQE therefore asks the `add-join`
// edit to alias the base table too (`FROM customers c`), and this planner:
//   - inserts ` <alias>` right after the relation's name token;
//   - rewrites every `<name>.` qualifier of that relation in the statement's
//     own clauses (SELECT list, WHERE, HAVING, GROUP BY, ORDER BY, existing
//     JOIN … ON) to `<alias>.` — once a table has an alias, its bare name no
//     longer qualifies columns on any engine;
//   - reports the renames, so the join's own ON text and the bare-column
//     qualifiers (`qualifyColumns`) use the alias as well.
//
// It aliases a relation only when EVERY `<name>.` in the statement is one it
// rewrites. A reference inside a nested subquery or CTE body (another scope,
// maybe another table of that name), a three-part `schema.table.column`, or a
// place the clause list above does not cover leaves that relation unaliased,
// and the join keeps the round-1 behaviour (qualify with the table name). So
// does a SELECT-only guard: UPDATE / DELETE targets are never renamed here.

import type { AddJoinClause, AliasRelation, FromItem, PlannedByteEdit } from '../types';

import { skipContextFor, trySkipNonCode, type SkipContext } from '../classify/tokenSkip';

import type { EditCtx } from './applyStructuralEdit';
import {
  isQuoteOpen,
  isWordStart,
  matchingParenEnd,
  readWord,
  skipWs,
} from './applyStructuralEdit.qualifyColumns';
import { insertEdit, replaceEdit } from './applyStructuralEdit.shared';

/** A name token followed by `.` — i.e. used as a qualifier. */
type QualifierToken = { readonly start: number; readonly end: number; readonly folded: string; readonly afterDot: boolean };

/**
 * Every qualifier token in `[start, end)`: a bare or quoted name directly
 * followed by `.`. `skipSubqueries` jumps over `(SELECT …)` / `(WITH …)`.
 */
export function scanQualifierTokens(
  source: string,
  start: number,
  end: number,
  skip: SkipContext,
  skipSubqueries: boolean,
): QualifierToken[] {
  const out: QualifierToken[] = [];
  let prevSignificant = '';
  let i = start;
  const consider = (tokStart: number, tokEnd: number, text: string): void => {
    const next = skipWs(source, tokEnd, end, skip);
    if (source[next] === '.') {
      out.push({ start: tokStart, end: tokEnd, folded: text.toLowerCase(), afterDot: prevSignificant === '.' });
    }
    prevSignificant = 'word';
  };
  while (i < end) {
    const c = source[i];
    if (/\s/.test(c)) {
      i++;
      continue;
    }
    if (isQuoteOpen(c, skip)) {
      const j = trySkipNonCode(source, i, end, skip);
      const inner = source.slice(i + 1, Math.max(i + 1, j - 1));
      consider(i, j, c === '"' ? inner.replace(/""/g, '"') : inner);
      i = j;
      continue;
    }
    const skipped = trySkipNonCode(source, i, end, skip);
    if (skipped !== -1) {
      if (c === "'" || c === '$') prevSignificant = 'literal';
      i = skipped;
      continue;
    }
    if (c === '(' && skipSubqueries) {
      const k = skipWs(source, i + 1, end, skip);
      const firstWord = source.slice(k, readWord(source, k, end)).toUpperCase();
      if (firstWord === 'SELECT' || firstWord === 'WITH') {
        i = matchingParenEnd(source, i, end, skip);
        prevSignificant = ')';
        continue;
      }
    }
    if (isWordStart(c)) {
      const j = readWord(source, i, end);
      consider(i, j, source.slice(i, j));
      i = j;
      continue;
    }
    prevSignificant = c;
    i++;
  }
  return out;
}

/** The statement's own clauses whose qualifiers the planner rewrites. */
function rewritableRanges(ctx: EditCtx): Array<readonly [number, number]> {
  const s = ctx.structure;
  const ranges = [
    ...(s.selectItems ?? []).map((i) => i.range),
    ...(s.where ?? []).map((f) => f.range),
    ...(s.having ?? []).map((f) => f.range),
    ...(s.groupBy ?? []).map((g) => g.range),
    ...(s.orderBy ?? []).map((o) => o.range),
    ...(s.joins ?? []).flatMap((j) => (j.on ?? []).map((f) => f.range)),
  ];
  return ranges.map((r) => [r.startOffset, r.endOffset] as const);
}

function relationFor(ctx: EditCtx, request: AliasRelation): FromItem | undefined {
  if (request.source === 'from') return ctx.structure.fromItems?.[request.index];
  return ctx.structure.joins?.[request.index]?.right;
}

function exposedNames(ctx: EditCtx): string[] {
  const items = [...(ctx.structure.fromItems ?? []), ...(ctx.structure.joins ?? []).map((j) => j.right)];
  return items.flatMap((item) => {
    if (item.alias) return [item.alias.alias.toLowerCase()];
    return item.target.kind === 'relation' ? [item.target.qualified.name.toLowerCase()] : [];
  });
}

export type AliasRelationsPlan = {
  readonly edits: PlannedByteEdit[];
  /** Folded exposed name → the alias it now has. */
  readonly renamed: ReadonlyMap<string, string>;
};

export function planAliasRelations(ctx: EditCtx, requests: readonly AliasRelation[]): AliasRelationsPlan {
  const renamed = new Map<string, string>();
  const edits: PlannedByteEdit[] = [];
  if (requests.length === 0 || ctx.structure.kind !== 'select') return { edits, renamed };

  const skip = skipContextFor(ctx.options);
  const stmt = ctx.statementInfo.range;
  const everywhere = scanQualifierTokens(ctx.source, stmt.startOffset, stmt.endOffset, skip, false);
  const rewritable = new Map<number, QualifierToken>();
  for (const [from, to] of rewritableRanges(ctx)) {
    for (const tok of scanQualifierTokens(ctx.source, from, to, skip, true)) {
      if (!tok.afterDot) rewritable.set(tok.start, tok);
    }
  }
  const taken = new Set(exposedNames(ctx));

  for (const request of requests) {
    const item = relationFor(ctx, request);
    if (!item || item.alias || item.target.kind !== 'relation') continue;
    const folded = item.target.qualified.name.toLowerCase();
    const alias = request.alias;
    // Two relations exposing the same name, or an alias already in use: skip.
    if (exposedNames(ctx).filter((n) => n === folded).length !== 1) continue;
    if (taken.has(alias.toLowerCase()) || renamed.has(folded)) continue;
    const all = everywhere.filter((t) => t.folded === folded);
    const mine = [...rewritable.values()].filter((t) => t.folded === folded);
    if (all.length !== mine.length) continue;

    edits.push(insertEdit(ctx, item.target.nameRange.endOffset, ` ${alias}`, 'add-join:alias-relation'));
    for (const tok of mine) edits.push(replaceEdit(ctx, tok.start, tok.end, alias, 'add-join:alias-qualifier'));
    renamed.set(folded, alias);
    taken.add(alias.toLowerCase());
  }
  return { edits, renamed };
}

/** `text` with every renamed relation's `name.` qualifier rewritten to its alias. */
export function rewriteQualifiersInText(
  text: string,
  renamed: ReadonlyMap<string, string>,
  skip: SkipContext,
): string {
  if (renamed.size === 0 || !text) return text;
  let out = text;
  const tokens = scanQualifierTokens(text, 0, text.length, skip, true)
    .filter((t) => !t.afterDot && renamed.has(t.folded))
    .sort((a, b) => b.start - a.start);
  for (const t of tokens) out = out.slice(0, t.start) + renamed.get(t.folded) + out.slice(t.end);
  return out;
}

/** The add-join clause in terms of the aliases the planner applied. */
export function withRenamedQualifiers(
  clause: AddJoinClause,
  renamed: ReadonlyMap<string, string>,
  skip: SkipContext,
): AddJoinClause {
  if (renamed.size === 0) return clause;
  return {
    ...clause,
    ...(clause.onText !== undefined ? { onText: rewriteQualifiersInText(clause.onText, renamed, skip) } : {}),
    ...(clause.qualifyColumns
      ? {
          qualifyColumns: clause.qualifyColumns.map((c) => ({
            ...c,
            qualifier: renamed.get(qualifierKey(c.qualifier)) ?? c.qualifier,
          })),
        }
      : {}),
  };
}

/**
 * A `qualifyColumns` qualifier's rename key: its name with any delimiters off
 * (`"order items"` / `[Order Details]` / `` `order items` ``) and lower-cased,
 * the same fold `scanQualifierTokens` gives the statement's own qualifiers.
 */
function qualifierKey(qualifier: string): string {
  const open = qualifier[0];
  const close = open === '[' ? ']' : open === '"' || open === '`' ? open : null;
  if (!close || qualifier.length < 2 || qualifier[qualifier.length - 1] !== close) return qualifier.toLowerCase();
  return qualifier.slice(1, -1).split(close + close).join(close).toLowerCase();
}

/**
 * Join zero-width inserts that land on the same offset (the alias after the
 * last FROM table and the new JOIN right behind it), in array order —
 * `emitSource` refuses two inserts at one offset.
 */
export function mergeSameOffsetInserts(edits: readonly PlannedByteEdit[]): PlannedByteEdit[] {
  const out: PlannedByteEdit[] = [];
  const insertAt = new Map<number, number>();
  for (const e of edits) {
    const zeroWidth = e.range.startOffset === e.range.endOffset;
    const existing = zeroWidth ? insertAt.get(e.range.startOffset) : undefined;
    if (existing !== undefined) {
      const prev = out[existing];
      out[existing] = { ...prev, replacement: prev.replacement + e.replacement, rationale: `${prev.rationale}+${e.rationale}` };
      continue;
    }
    if (zeroWidth) insertAt.set(e.range.startOffset, out.length);
    out.push(e);
  }
  return out;
}
